import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { business } from "@/data/site";
import { CallNowButton, EstimateButton } from "@/components/CTAButtons";
import { track } from "@/lib/analytics";

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  {
    label: "Services",
    to: "/services",
    children: [
      { label: "All Services", to: "/services" },
      { label: "Plumbing", to: "/services/plumbing" },
      { label: "Electrical", to: "/services/electrical" },
      { label: "HVAC", to: "/services/hvac" },
    ],
  },
  { label: "Our Work", to: "/projects" },
  { label: "Reviews", to: "/reviews" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#E2E8F0]" data-testid="site-header">
      {/* Top emergency strip */}
      <div className="bg-[#0F2E4C] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <Link to="/emergency" className="flex items-center gap-2 hover:text-[#34D399] transition-colors" data-testid="header-emergency-link">
            <span className="w-2 h-2 bg-[#059669] rounded-full animate-pulse" />
            <span className="font-semibold uppercase tracking-wide">{business.emergencyLine}</span>
          </Link>
          <a href={business.phoneHref} onClick={() => track("phone_tap", { location: "header" })} className="hidden sm:flex items-center gap-2 font-bold hover:text-[#34D399] transition-colors" data-testid="header-top-phone">
            <Phone className="w-4 h-4" /> {business.phoneDisplay}
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <div className="w-11 h-11 bg-[#059669] flex items-center justify-center">
            <span className="text-white font-display font-black text-2xl">E</span>
          </div>
          <div className="leading-none">
            <div className="font-display font-black text-lg sm:text-xl text-[#0F2E4C] tracking-tight">Empire</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] font-semibold">Home Services</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-semibold flex items-center gap-1 transition-colors ${isActive ? "text-[#059669]" : "text-[#0F2E4C] hover:text-[#059669]"}`
                  }
                >
                  {item.label} <ChevronDown className="w-3.5 h-3.5" />
                </NavLink>
                <div className="absolute top-full left-0 w-52 bg-white border border-[#E2E8F0] shadow-[4px_4px_0px_#0F2E4C] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  {item.children.map((c) => (
                    <Link key={c.to} to={c.to} className="block px-4 py-3 text-sm font-medium text-[#0F2E4C] hover:bg-[#0F2E4C] hover:text-white transition-colors" data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-[#059669]" : "text-[#0F2E4C] hover:text-[#059669]"}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <CallNowButton size="sm" testid="header-call-btn" />
          <EstimateButton size="sm" label="Free Estimate" testid="header-estimate-btn" />
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 text-[#0F2E4C]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav className="lg:hidden bg-white border-t border-[#E2E8F0] max-h-[70vh] overflow-y-auto" data-testid="mobile-nav">
          {navItems.map((item) => (
            <div key={item.label} className="border-b border-[#E2E8F0]">
              {item.children ? (
                <>
                  <button
                    className="w-full flex items-center justify-between px-4 py-4 text-base font-semibold text-[#0F2E4C]"
                    onClick={() => setServicesOpen(!servicesOpen)}
                    data-testid="mobile-services-toggle"
                  >
                    {item.label} <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen &&
                    item.children.map((c) => (
                      <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block pl-8 pr-4 py-3 text-sm font-medium text-[#475569] hover:text-[#059669]">
                        {c.label}
                      </Link>
                    ))}
                </>
              ) : (
                <Link to={item.to} onClick={() => setOpen(false)} className="block px-4 py-4 text-base font-semibold text-[#0F2E4C] hover:text-[#059669]" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <div className="p-4 flex flex-col gap-3">
            <CallNowButton className="w-full" />
            <EstimateButton className="w-full" />
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
